import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Helper function to show a toast notification
const showToast = (message, duration = 3000) => {
  const toast = document.createElement('div');
  toast.style.position = 'fixed';
  toast.style.bottom = '20px';
  toast.style.left = '50%';
  toast.style.transform = 'translateX(-50%)';
  toast.style.backgroundColor = '#333';
  toast.style.color = 'white';
  toast.style.padding = '12px 24px';
  toast.style.borderRadius = '4px';
  toast.style.zIndex = '1000';
  toast.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
  toast.textContent = message;
  
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.style.transition = 'opacity 0.5s';
    toast.style.opacity = '0';
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 500);
  }, duration);
};

// Initialize the app
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Register service worker after the app is loaded
const registerServiceWorker = () => {
  // In development, register with no-caching
  if (process.env.NODE_ENV !== 'production') {
    console.log('Running in development mode - registering service worker with no-caching');
    serviceWorkerRegistration.register({
      onUpdate: (registration) => {
        console.log('Service worker update available');
        registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
      },
      onSuccess: (registration) => {
        console.log('Service worker registration successful with scope: ', registration?.scope);
      },
      onError: (error) => {
        console.error('Service worker registration failed:', error);
      }
    });
    return;
  }

  // In production, register with full PWA capabilities
  console.log('Running in production mode - registering service worker');
  serviceWorkerRegistration.register({
    onUpdate: (registration) => {
      if (registration?.waiting) {
        const shouldUpdate = window.confirm(
          'A new version of the app is available. Would you like to update now?'
        );
        if (shouldUpdate) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    },
    onSuccess: (registration) => {
      if (registration && typeof registration.update === 'function') {
        console.log('Service worker registration successful with scope: ', registration.scope);
        showToast('App is ready for offline use');
        
        // Check for updates every hour
        setInterval(() => {
          registration.update().catch(err => {
            console.warn('Service worker update check failed:', err);
          });
        }, 60 * 60 * 1000);
        
        // Also check for updates when the page regains focus
        window.addEventListener('focus', () => {
          if (typeof registration.update === 'function') {
            registration.update().catch(err => {
              console.warn('Service worker update check failed on focus:', err);
            });
          }
        });
      } else if (registration) {
        console.log('Service worker registration successful, but update method not available');
      }
    },
    onError: (error) => {
      console.error('Service worker registration failed:', error);
      showToast('Failed to register service worker');
    }
  });
};

// Function to handle service worker updates
const handleServiceWorkerUpdate = () => {
  // Check if there's a new service worker waiting to be activated
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('New service worker activated, reloading...');
      window.location.reload();
    });
  }
};

// Register service worker and set up update handling
const initializeServiceWorker = () => {
  handleServiceWorkerUpdate();
  registerServiceWorker();
};

// Wait for the window to load before initializing the service worker
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  // If the document is already loaded, initialize the service worker immediately
  setTimeout(initializeServiceWorker, 0);
} else {
  // Otherwise, wait for the window to load
  window.addEventListener('load', registerServiceWorker);
}

// Listen for service worker messages
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // This fires when the service worker controlling this page changes
    // (e.g., after a new service worker has been installed and activated)
    window.location.reload();
  });
}

// Report web vitals
reportWebVitals();
